import { connect, FormikValues } from 'formik'
import { boolean, string } from 'yup'

import Label from '@/components/Label'
import Checkbox from '@/components/TailwindUI/Checkbox'
import RadioGroup from '@/components/TailwindUI/RadioGroup'
import Toggle from '@/components/TailwindUI/Toggle'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { FieldTypes, StepData, UserLookupData } from '@/pages/SpaceAppBuilder/utilities/interfaces'

type OutputProps = {
  readOnly?: boolean
}

const getValidationSchema = () => {
  return {
    searchMode: string(),
    givenName: string()
      .trim()
      .when(['matchFirstAndLastName', 'searchMode'], {
        is: (matchFirstAndLastName, searchMode) => matchFirstAndLastName && searchMode === 'name',
        then: string().required('First name is required when searching full name')
      }),
    familyName: string()
      .trim()
      .when(['matchFirstAndLastName', 'searchMode'], {
        is: (matchFirstAndLastName, searchMode) => matchFirstAndLastName && searchMode === 'name',
        then: string().required('Last name is required when searching full name')
      }),
    matchFirstAndLastName: boolean(),
    userId: string().when('searchMode', {
      is: 'user ID',
      then: string().required('User ID is required')
    }),
    email: string().when('searchMode', {
      is: 'email',
      then: string().required('Email address is required')
    }),
    filter: string().when('searchMode', {
      is: 'filter',
      then: string().required('SCIM filter is required')
    }),
    getManager: boolean(),
    getDirectReports: boolean(),
    getPeers: boolean()
  }
}

const getInitialValues = (stepData: UserLookupData): StepData => ({
  searchMode: stepData.searchMode || 'name',
  givenName: stepData.givenName || '',
  familyName: stepData.familyName || '',
  matchFirstAndLastName: stepData.matchFirstAndLastName || false,
  userId: stepData.userId || '',
  email: stepData.email || '',
  filter: stepData.filter || '',
  getManager: stepData.getManager || false,
  getDirectReports: stepData.getDirectReports || false,
  getPeers: stepData.getPeers || false
})

const searchModes = ['name', 'email', 'user ID', 'filter']

const getUserLookupOperationHelpUrl = 'https://help.workgrid.com/s/article/Log' // TODO

const UserLookupOperation = ({ formik, readOnly = false }: OutputProps & FormikValues) => {
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const { setFieldValue, values } = formik
  const searchMode = values.stepData.searchMode

  return (
    <div data-testid="user-lookup-property-panel">
      <h2 className="text-sm font-semibold leading-6 text-gray-900">Search by</h2>
      <RadioGroup
        name="searchMode"
        value={searchMode}
        options={searchModes}
        onChange={value => setFieldValue('stepData.searchMode', value)}
        disabled={readOnly}
        className="mt-2 px-1"
      />
      <div className="mt-4">
        {searchMode === 'name' ? (
          <>
            <Toggle
              className="mb-2 flex items-center justify-between pr-3"
              onChange={() => setFieldValue(`stepData.matchFirstAndLastName`, !values.stepData.matchFirstAndLastName)}
              checked={values.stepData.matchFirstAndLastName}
              disabled={readOnly}
              label={
                <div className="mt-1 mb-2 text-sm text-gray-500">
                  <Label>Full name match</Label>
                  {values.stepData.matchFirstAndLastName ? (
                    <div>
                      <div>Searched users will match both first name AND last name.</div>
                      <div>Turn off to match either name.</div>
                    </div>
                  ) : (
                    <div>
                      <div>Searched users will match either first name OR last name.</div>
                      <div>Turn on to match both.</div>
                    </div>
                  )}
                </div>
              }
            />
            <VariableInput
              required={values.stepData.matchFirstAndLastName}
              readOnly={readOnly}
              variables={variables}
              name="stepData.givenName"
              label="First Name"
              placeholder="Enter the first name"
              validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
            />
            <VariableInput
              required={values.stepData.matchFirstAndLastName}
              readOnly={readOnly}
              variables={variables}
              name="stepData.familyName"
              label="Last Name"
              placeholder="Enter the last name"
              validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
            />
          </>
        ) : searchMode === 'email' ? (
          <VariableInput
            required
            readOnly={readOnly}
            variables={variables}
            name="stepData.email"
            label="Email"
            placeholder="Enter the email address"
            validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
          />
        ) : searchMode === 'filter' ? (
          <VariableInput
            required
            readOnly={readOnly}
            variables={variables}
            name="stepData.filter"
            label="SCIM Filter"
            placeholder="Enter a SCIM filter to use"
            validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
          />
        ) : (
          <VariableInput
            required
            readOnly={readOnly}
            variables={variables}
            name="stepData.userId"
            label="User ID"
            placeholder="Enter the user ID"
            validVariableTypes={[FieldTypes.STRING, FieldTypes.ANY]}
          />
        )}
      </div>
      <Label className="mt-2">Include in result:</Label>
      <div className="mt-1 text-sm text-gray-500">
        Select additional users, relative to the queried user, to include in the response.
      </div>
      <div className="mt-4">
        <Checkbox
          onChange={() => setFieldValue(`stepData.getManager`, !values.stepData.getManager)}
          checked={values.stepData.getManager}
          disabled={readOnly}
          label="Manager"
        />
      </div>
      <div className="mt-2">
        <Checkbox
          onChange={() => setFieldValue(`stepData.getDirectReports`, !values.stepData.getDirectReports)}
          checked={values.stepData.getDirectReports}
          disabled={readOnly}
          label="Direct Reports"
        />
      </div>
      <div className="mt-2">
        <Checkbox
          onChange={() => setFieldValue(`stepData.getPeers`, !values.stepData.getPeers)}
          checked={values.stepData.getPeers}
          disabled={readOnly}
          label="Peers"
        />
      </div>
      <div className="mt-4 text-sm text-gray-500">(Note: For better performance, only request what you need.)</div>
    </div>
  )
}

export default connect(UserLookupOperation)

export { getInitialValues, getValidationSchema, getUserLookupOperationHelpUrl }
